import * as Sentry from '@sentry/react';
import {
  createStyles,
  makeStyles,
  Theme,
  Button,
  Box,
  Divider,
  Snackbar,
} from "@material-ui/core";
import React from "react";
import { connect } from "react-redux";
import { APIState } from "../utils/api/reducer";
import {
  ContestPlayer,
  LateSwapLineup,
  LateSwapLineupResponse,
  StackConstraint,
} from "./store/types";
import DataGrid from "../common/Table/newTable";
import { APIConstants } from "../utils/api/constants";
import ProgressBar from "../preContest/progressBar";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import ExposureIcon from '@material-ui/icons/Exposure';
import { CSVLink } from "react-csv";
import {
  getContestLineups,
  getContestPlayers,
  removeBoostedPlayer,
  setBoostedPlayer,
  setExportableLineups,
  setLiveContestId,
  setPercentToFirst,
  setPoolSize,
  startLoading,
  triggerContestGen,
} from "./store/actions";
import { getExposuresKeyLateSwap, getExposuresKeyLineupPlayer, getSiteForShowdown } from "../utils/showdownHandler";
import { GridApiPro } from "@mui/x-data-grid-pro/models/gridApiPro";
import RefreshIcon from "@material-ui/icons/Refresh";
import { Alert, Stack } from "@mui/material";
import Upload from "../preContest/upload";
import { contestGeneratorPlayerHeaderConfig } from "../common/Table/ContestGeneratorConfig";
import { useLocalStorage } from "usehooks-ts";

import { ContestLineup } from "../contestGenerator/store/types";
import QuickFavorite from "./QuickFavorite";
import { AuthResponse } from "../preContest/store/types";
import { MenuOption, SelectInput, poolSourceOptions } from "../common/selectOptions";
import { MoonLoader } from "react-spinners";
import FinalStepTabs from "./FinalStepTabs";
import LateSwapPlayerGroupsModal from "./LateSwapPlayerGroupsModal";
import LateSwapPlayerManagementModal from "./LateSwapPlayerManagementModal";
import LateSwapStackManagementModal from "./LateSwapStackManagementModal";
import LineupSettings from "./LineupSettings";
import EasyProjection from "../common/EasyProjection";
import { Projection } from "../common/EasyProjection/types";
import { SitesResponse, SlateResponse, SportsResponse } from "../common/store/types";
import { Layers, LayersClear } from "@material-ui/icons";
import { getFilename } from "../common/helpers/FilenameHelper";
import { useGetLiveContests } from '../common/queries/contestQueries';
import { useUserId } from '../utils/authentication';
import { CustomToolTip, toolTipText } from '../common/CustomToolTip';
import { StackExposures } from '../preContest/queries/queryTypes';
import { showStackExposuresPreContest } from '../utils/stackConstants';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    heading: {
      width: "100%",
      maxHeight: "100%",
      borderRadius: 10,
      '& .MuiDivider-root': {
        backgroundColor: 'rgba(251, 255, 57, 0.30)',
      }
    },
    generatorSettings: {
      backgroundColor: "rgba(219, 77, 0, 0.20)",
      borderRadius: 10,
      padding: "0px 8px",
      '& .MuiStepIcon-active': {
        color: '#0058CC',
      }
    },
    active: {
      backgroundColor: "rgba(219, 77, 0, 0.20)",
    },
    buttonWrapper: {
      position: "absolute",
      right: 0,
      display: "flex",
      alignItems: "center",
      paddingRight: "8px",
    },
    exportButton: {
      height: 40,
      borderRadius: 3,
      backgroundColor: 'rgba(219, 77, 0, .7)',
      textWrap: 'nowrap',
      padding: "10px 16px",
      margin: "4px 0",
      marginLeft: 8,
      "& a": {
        color: "white !important",
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
      },
      "&:hover": {
        color: "white",
        backgroundColor: 'rgba(219, 77, 0, 0.70)',
      },
      "& .MuiSvgIcon-root": {
        marginLeft: 4,
        fontSize: "1rem",
      },
    },
    exportButtonCG: {
      height: 40,
      borderRadius: 3,
      textWrap: 'nowrap',
      padding: "10px 16px",
      margin: "4px 0",
      marginLeft: 8,
      "& a": {
        color: "white !important",
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
      },
      "& .MuiSvgIcon-root": {
        marginLeft: 4,
        fontSize: "1rem",
      },
    },
    goToPre: {
      display: "flex",
      padding: "8px 16px",
      justifyContent: "center",
      alignItems: "center",
      gap: "4px",
      color: "white",
      textDecoration: "none",
      backgroundColor: "rgba(219, 77, 0, 0.70)",
      borderRadius: "3px",
      textTransform: "uppercase",
      fontSize: 12,
      fontWeight: 500,
      "& .MuiSvgIcon-root": {
        color: "#7CA9FF !important",
      },
      "&:hover": {
        color: "white",
        backgroundColor: "rgba(219, 77, 0, 0.70)",
        "& .MuiSvgIcon-root": {
          color: "white !important",
        },
      },
    },
    lateSwapOptions: {
      overflowY: 'auto',
    },
    disabled: {
      pointerEvents: "none",
      color: "white !important",
      backgroundColor: "#555555 !important",
      "& .MuiSvgIcon-root": {
        color: "white !important",
      },
    },
    button: {
      borderRadius: 3,
      background: 'rgba(219, 77, 0, 0.70)',
      color: 'white',
      padding: '8px 16px',
      marginLeft: 8,
      '&:hover': {
        background: 'rgba(219, 77, 0, 0.70)',
      },
      '&:disabled': {
        backgroundColor: '#555555 !important',
        color: '#BEBEBE'
      }
    },
    activeBoostButton: {
      border: '1px solid rgba(219, 77, 0, 0.70)',
      color: 'white',
      lineHeight: '1',
      '&:hover': {
        backgroundColor: "rgba(219, 77, 0, 0.70)",
      },
    },
    regenerate: {
      display: "flex",
      justifyContent: "center",
      padding: "8px 16px",
      alignItems: "center",
      gap: "4px",
      backgroundColor: "rgba(219, 77, 0, 0.90)",
      paddingRight: "8px",
      color: "white",
      textDecoration: "none",
      borderRadius: "3px",
      textTransform: "uppercase",
      fontSize: 14,
      fontWeight: 500,
      "&:hover": {
        color: "white",
        backgroundColor: "rgba(219, 77, 0, 0.90)",
      },
      "&:disabled": {
        color: "white !important",
        backgroundColor: "#555555 !important",
        "& .MuiSvgIcon-root": {
          color: "white !important",
        },
      },
    },
  })
);

function getStepContent(stepIndex: number) {
  switch (stepIndex) {
    case 4:
      return 'Use quick favorite to quickly select the best swappable lineups based on your preference. Expand each lineup to view and select other options to export.';
    default:
      return '';
  }
}


interface OwnProps {
  readonly loadingPercent: string;
  readonly setStatusType: (type: string) => void;
  readonly useCustomProjections: boolean;
  readonly activeStep: number;
  readonly setActiveStep: (newStep: number) => void;
}

interface ReduxProps {
  readonly contestPlayers: APIState<ContestPlayer[]>;
  readonly contestGenLineups: APIState<ContestLineup[]>;
  readonly authenticated: APIState<AuthResponse>;
  readonly contestLineups: APIState<LateSwapLineupResponse>;
  readonly loading: boolean;
  readonly generatedSlate: SlateResponse;
  readonly boostedPlayers: { [key: string]: number };
  readonly stackConstraints: StackConstraint[];
  readonly poolSize: number;
  readonly runback: StackConstraint;
  readonly selectedSport: SportsResponse;
  readonly selectedSite: SitesResponse;
  readonly selectedSlate: SlateResponse;
  readonly diversity: number;
  readonly lineupDiversity: number;
  readonly percentToFirst: number;
  readonly liveContestId: string;
}

interface DispatchProps {
  readonly SetExportableLineups: (lineups: string) => void;
  readonly SetBoostedPlayer: (player: string, boostedValues: number) => void;
  readonly RemoveBoostedPlayer: (player: string, boostedValues: number) => void;
  readonly SetPoolSize: (poolSize: number) => void;
  readonly SetPercentToFirst: (percent: number) => void;
  readonly SetLiveContestId: (contestId: string) => void;
  readonly StartLoading: () => void;
  readonly GetContestPlayers: (slateId: string, user: string, liveContestId?: string, totalEntries?: number) => void;
  readonly GetContestLineups: (slateId: string, user: string, liveContestId?: string, totalEntries?: number) => void;
  readonly TriggerContestGen: (
    slateId: string,
    user: string,
    totalLineups: number,
    boostedPlayers: { [key: string]: number },
    stackConstraints: StackConstraint[],
    runbackPercent: number,
    lateSwap?: boolean,
    useCustomProjetions?: boolean,
    diversity?: number,
  ) => void;
}

type Props = OwnProps & ReduxProps & DispatchProps;

const TabsWrapper = (props: Props) => {
  const classes = useStyles();
  const [lineupFile, setLineupFile] = React.useState("");
  const [contests, setContests] = React.useState<MenuOption<any>[]>([]);
  const [selectedContest, setSelectedContest] = React.useState<MenuOption<any>>({} as MenuOption<any>);
  const [onLoadDiversity, setOnLoadDiversity] = React.useState<number | number[]>(0);
  const [onLoadLineupDiversity, setOnLoadLineupDiversity] = React.useState<number | number[]>(0);
  const [exportableData, setExportableData] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [contestSimulating, setContestSimulating] = React.useState(false);
  const [lineupFileErrors, setLineupFileErrors] = React.useState([] as string[])
  const [lineupVerified, setLineupVerified] = React.useState(false);
  const [successSnack, setSuccessSnack] = React.useState(false);
  const [parsingContest, setParsingContest] = React.useState(false);
  const [exportCsvHeader, setExportCsvHeader] = React.useState("");
  const [playerModalOpen, setPlayerModalOpen] = React.useState(false);
  const [stackModalOpen, setStackModalOpen] = React.useState(false);
  const [groupsModalOpen, setGroupsModalOpen] = React.useState(false);
  const [playerList, setPlayerList] = React.useState<{ [key: string]: number }>({});
  const [stackList, setStackList] = React.useState<{ [key: string]: number }>({});
  const [playerExposureCaps, setPlayerExposureCaps] = React.useState<{ [key: string]: number }>({});
  const [stackExposureCaps, setStackExposureCaps] = React.useState<{ [key: string]: number }>({});
  const [playerGroups, setPlayerGroups] = useLocalStorage<{ id: string; players: any[]; roiBoost: number }[]>(
    "lateswap-playerGroups",
    []
  );
  const [allowDupes, setAllowDupes] = React.useState(false);
  const [poolSource, setPoolSource] = React.useState('GeneratePool');
  const userId = useUserId();
  const auth = 'awe' + userId;
  const [useLiveProjections, setUseLiveProjections] = React.useState(true);
  const [customProjections, setCustomProjections] = React.useState<Projection[]>([]);
  const [tableApiRef, setTableApiRef] = React.useState<React.MutableRefObject<GridApiPro> | null>(null);

  const [savedLineups, setSavedLineups] = useLocalStorage<LateSwapLineup[]>(
    "lateswap-lineups",
    []
  );

  const {
    contestPlayers,
    contestLineups,
    authenticated,
    loading,
    loadingPercent,
    generatedSlate,
    boostedPlayers,
    stackConstraints,
    poolSize,
    liveContestId,
    percentToFirst,
    runback,
    selectedSite,
    selectedSlate,
    useCustomProjections,
    contestGenLineups,
    activeStep,
    diversity,
    lineupDiversity,
    setActiveStep,
    setStatusType,
    SetBoostedPlayer,
    RemoveBoostedPlayer,
    TriggerContestGen,
    SetPoolSize,
    StartLoading,
    SetLiveContestId,
    GetContestPlayers,
  } = props;
  const lastGeneratedBoosts = React.useRef({});
  const lastGeneratedStackConstraints = React.useRef({});
  const { data: liveContests } = useGetLiveContests(selectedSlate?.slateId?.toString(), selectedSlate?.slateId === undefined);
  const liveContestReady = liveContests !== undefined && liveContests.length > 0;
  const handleBoost = (
    playerAndId: string,
    boostValue: number,
    removePlayer?: boolean
  ) => {
    if (removePlayer) {
      SetBoostedPlayer(playerAndId, boostValue);
    }
    if (boostValue === 0 && !removePlayer) {
      RemoveBoostedPlayer(playerAndId, boostValue);
    } else {
      SetBoostedPlayer(playerAndId, boostValue);
    }
  };

  const stacks = () => {
    const stackExposuresDict: { [key: string]: StackExposures } = {};
    const savedLineupsCount = savedLineups.length;
    const preSwapStackExposuresDict: { [key: string]: StackExposures } = {};

    contestLineups.status === APIConstants.SUCCEEDED &&
      contestLineups?.data?.lateSwapData.forEach((lineup) => {
        lineup?.stacks?.forEach((stack) => {
          var exp = {
            ThreeMLBOneNFLStackExposureCount: 0,
            FourMLBTwoNFLStackExposureCount: 0,
            FiveMLBThreeNFLStackExposureCount: 0,
          } as StackExposures;

          if (stackExposuresDict[stack.team]) {
            exp = stackExposuresDict[stack.team];
          }

          if (
            (generatedSlate.sport?.toLowerCase() === "mlb" && stack.count > 2) ||
            (['cfb', 'nfl'].includes(generatedSlate.sport?.toLowerCase()) && stack.count > 0)
          ) {
            stackExposuresDict[stack.team] = exp;
          }
        });
      });

    savedLineups.forEach((lineup) => {
      lineup?.stacks?.forEach((stack) => {
        var exp = {
          QbName: stack.qbName,
          ThreeMLBOneNFLStackExposureCount: 0,
          FourMLBTwoNFLStackExposureCount: 0,
          FiveMLBThreeNFLStackExposureCount: 0,
        } as StackExposures;

        if (stackExposuresDict[stack.team]) {
          exp = stackExposuresDict[stack.team];
        }
        const stackAndSport = `${stack.count}|${generatedSlate?.sport}`;
        switch (stackAndSport) {
          case "1|NFL":
          case "1|CFB":
          case "3|MLB":
            exp.ThreeMLBOneNFLStackExposureCount =
              exp.ThreeMLBOneNFLStackExposureCount + 1;
            break;
          case "2|NFL":
          case "2|CFB":
          case "4|MLB":
            exp.FourMLBTwoNFLStackExposureCount =
              exp.FourMLBTwoNFLStackExposureCount + 1;
            break;
          case "3|NFL":
          case "3|CFB":
          case "5|MLB":
            exp.FiveMLBThreeNFLStackExposureCount =
              exp.FiveMLBThreeNFLStackExposureCount + 1;
            break;
          default:
            break;
        }
        if (
          (generatedSlate?.sport?.toLowerCase() === "mlb" && stack.count > 2) ||
          (['nfl', 'cfb'].includes(generatedSlate?.sport?.toLowerCase()) && stack.count > 0)
        ) {
          stackExposuresDict[stack.team] = exp;
        }
      });
    });

    contestLineups.data.lateSwapData.forEach((lineup) => {
      lineup?.stacks.forEach((stack) => {
        var exp = {
          QbName: stack.qbName,
          ThreeMLBOneNFLStackExposureCount: 0,
          FourMLBTwoNFLStackExposureCount: 0,
          FiveMLBThreeNFLStackExposureCount: 0,
        } as StackExposures;
        if (preSwapStackExposuresDict[stack.team]) {
          exp = preSwapStackExposuresDict[stack.team];
        }
        const stackAndSport = `${stack.count}|${selectedSlate?.sport}`;
        switch (stackAndSport) {
          case "1|NFL":
          case "1|CFB":
          case "3|MLB":
            exp.ThreeMLBOneNFLStackExposureCount =
              exp.ThreeMLBOneNFLStackExposureCount + 1;
            break;
          case "2|NFL":
          case "2|CFB":
          case "4|MLB":
            exp.FourMLBTwoNFLStackExposureCount =
              exp.FourMLBTwoNFLStackExposureCount + 1;
            break;
          case "3|NFL":
          case "3|CFB":
          case "5|MLB":
            exp.FiveMLBThreeNFLStackExposureCount =
              exp.FiveMLBThreeNFLStackExposureCount + 1;
            break;
          default:
            break;
        }
        if (['nfl', 'cfb'].includes(selectedSlate?.sport?.toLowerCase()) || stack.count > 2) {
          preSwapStackExposuresDict[stack.team] = exp;
        }
      });
    });

    if (Object.keys(stackExposuresDict).length > 0) {
      const stacks = Object.keys(stackExposuresDict).map((team) => {
        const stackCounts = stackExposuresDict[team];
        const preSwapStackCounts = preSwapStackExposuresDict[team];

        return {
          id: team,
          team: team,
          ThreeMLBOneNFLStackExposureCount:
            stackCounts.ThreeMLBOneNFLStackExposureCount,
          ThreeMLBOneNFLStackExposurePct:
            stackCounts.ThreeMLBOneNFLStackExposureCount / savedLineupsCount,
          FourMLBTwoNFLStackExposureCount:
            stackCounts.FourMLBTwoNFLStackExposureCount,
          FourMLBTwoNFLStackExposurePct:
            stackCounts.FourMLBTwoNFLStackExposureCount / savedLineupsCount,
          FiveMLBThreeNFLStackExposureCount:
            stackCounts.FiveMLBThreeNFLStackExposureCount,
          FiveMLBThreeNFLStackExposurePct:
            stackCounts.FiveMLBThreeNFLStackExposureCount / savedLineupsCount,
          PreSwapThreeMLBOneNFLStackExposureCount:
            preSwapStackCounts?.ThreeMLBOneNFLStackExposureCount,

          PreSwapThreeMLBOneNFLStackExposurePct:
            preSwapStackCounts?.ThreeMLBOneNFLStackExposureCount / contestLineups.data.lateSwapData.length,

          PreSwapFourMLBTwoNFLStackExposureCount:
            preSwapStackCounts?.FourMLBTwoNFLStackExposureCount,

          PreSwapFourMLBTwoNFLStackExposurePct:
            preSwapStackCounts?.FourMLBTwoNFLStackExposureCount / contestLineups.data.lateSwapData.length,

          PreSwapFiveMLBThreeNFLStackExposureCount:
            preSwapStackCounts?.FiveMLBThreeNFLStackExposureCount,

          PreSwapFiveMLBThreeNFLStackExposurePct:
            preSwapStackCounts?.FiveMLBThreeNFLStackExposureCount / contestLineups.data.lateSwapData.length,
        } as StackExposures;
      });

      return stacks;
    }

    return [];
  };

  const stackConstraintsStringCompare = () => {
    return stackConstraints.map(x => `${x.primary}-${x.secondary}-${x.percent}`).join();
  }

  const boostPlayers = () => {
    let players = contestLineups.data.players;
    if (
      savedLineups?.length > 0 &&
      contestLineups.status === APIConstants.SUCCEEDED
    ) {
      const playerCounts: { [key: string]: number } = {};
      savedLineups.forEach((x) =>
        x?.fullLineup
          ?.split(",")
          ?.forEach(
            (x, index) =>
            (playerCounts[
              getExposuresKeyLineupPlayer(
                x.trim(),
                index,
                selectedSlate?.type,
                selectedSlate?.sport,
                selectedSlate?.site
              )
            ] = playerCounts[
              getExposuresKeyLineupPlayer(
                x.trim(),
                index,
                selectedSlate?.type,
                selectedSlate?.sport,
                selectedSlate?.site
              )
            ]
                ? playerCounts[
                getExposuresKeyLineupPlayer(
                  x.trim(),
                  index,
                  selectedSlate?.type,
                  selectedSlate?.sport,
                  selectedSlate?.site
                )
                ] + 1
                : 1)
          )
      );

      players?.forEach((x) => {
        if (playerCounts[getExposuresKeyLateSwap(x)]) {
          x.postSwapExposure =
            (playerCounts[getExposuresKeyLateSwap(x)] / savedLineups.length) * 100;
          x.postSwapLeverage =
            (x.postSwapExposure - x.ownership) * 100;
        }
        else {
          x.postSwapExposure = 0;
          x.postSwapLeverage = 0 - x.ownership;
        }
      });
      return players.filter(x => !x.locked);
    } else if (
      savedLineups?.length === 0 &&
      contestLineups.status === APIConstants.SUCCEEDED
    ) {
      players?.forEach((x) => {
        x.postSwapExposure = 0;
        x.postSwapLeverage = 0;
      });
      return players.filter(x => !x.locked);
    }
    return [];
  };

  const handleRegenerate = () => {
    setOnLoadLineupDiversity(lineupDiversity);
    setOnLoadDiversity(diversity);
    TriggerContestGen(
      generatedSlate?.slateId,
      auth,
      poolSize,
      boostedPlayers,
      generatedSlate.type?.toUpperCase() === "SHOWDOWN" ? [] : stackConstraints,
      runback.percent,
      undefined,
      undefined,
      diversity
    );
    lastGeneratedBoosts.current = boostedPlayers;
    lastGeneratedStackConstraints.current = stackConstraintsStringCompare();
  };

  const onGenerate = () => {
    if (poolSource === 'LiveContest') {
      setContestSimulating(true);
      GetContestPlayers(selectedSlate?.slateId?.toString(), auth, liveContestId, poolSize);
    }
    else {
      // ClearBoostedPlayers();
      TriggerContestGen(selectedSlate?.slateId, auth, poolSize, boostedPlayers, selectedSlate.type?.toUpperCase() === 'SHOWDOWN' ? [] : stackConstraints, selectedSlate.type?.toUpperCase() === 'SHOWDOWN' ? 0 : runback.percent, true, useCustomProjections, diversity);
      // SetGeneratedSlate(selectedSlate);
      setContestSimulating(true);
    }

  }

  const onSimulate = async (projections: Projection[]) => {
    // Store projections with maxExposure for later use in exposure modal
    setCustomProjections(projections);

    const request = {
      user: auth,
      slateId: selectedSlate.slateId,
      contestType: "PreContest",
      totalEntries: poolSize,
      liveContestId: poolSource === 'LiveContest' ? liveContestId : undefined,
      percentToFirst,
      lateSwap: true,
      useCustomProjections: useCustomProjections,
      useLiveProjections: useLiveProjections,
      diversity: lineupDiversity,
      projections
    }
    await fetch(`${process.env.REACT_APP_API_URL}/api/contests/simulateLambdaContest`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request)
    }).then((res) => {
      setSavedLineups([]);
      setStatusType('LateSwap');
      StartLoading();
      setIsLoading(true)
      setActiveStep(3);
    });
  }


  const footer = (apiRef: React.MutableRefObject<GridApiPro>) => {
    return (
      <Stack className={classes.buttonWrapper} spacing={1} direction="row">
        {
          activeStep === 1 && (
            <>
              {poolSource !== 'LiveContest' && (
                <>
                  <Button
                    className={classes.exportButtonCG}
                    disabled={contestPlayers.data?.length === 0}
                    variant="text"
                  >
                    <CSVLink
                      // data={apiRef.current.getDataAsCsv()}
                      data={exportableData}
                      filename={getFilename(selectedSlate, '_Contest_Generator_Lineups_Late_Swap')}
                      target="_blank"
                    >
                      Export to CSV
                    </CSVLink>
                  </Button>
                  <Button
                    className={classes.regenerate}
                    disabled={
                      (boostedPlayers === lastGeneratedBoosts.current ||
                        (Object.keys(boostedPlayers).length === 0 &&
                          Object.keys(lastGeneratedBoosts.current).length === 0))
                      && diversity === onLoadDiversity
                      && lineupDiversity === onLoadLineupDiversity
                    }
                    variant="text"
                    onClick={() => handleRegenerate()}
                  >
                    Regenerate Lineups
                    <RefreshIcon />
                  </Button>
                </>
              )}

              <Button
                onClick={() => {
                  // onSimulate()
                  setActiveStep(2);
                }}
                className={
                  classes.goToPre +
                  (!(
                    JSON.stringify(boostedPlayers) === JSON.stringify(lastGeneratedBoosts.current) ||
                    (Object.keys(boostedPlayers).length === 0 &&
                      Object.keys(lastGeneratedBoosts.current).length === 0)
                  )
                    ? " " + classes.disabled
                    : "")
                }
              >
                Review Projections
                <ArrowForwardIcon />
              </Button>
            </>
          )
        }
      </Stack>
    );
  };

  React.useEffect(() => {
    const updateCsvHeader = async () => {
      if (selectedSlate && selectedSlate?.site) {
        const header = await fetch(
          `${process.env.REACT_APP_API_URL}/api/contests/csvHeaders?sport=${selectedSlate?.sport || 'NBA'}&site=${selectedSlate?.site}&lateSwap=true`
        ).then((res) => res.json());
        setExportCsvHeader(header);
      }
    }
    updateCsvHeader();
  }, [selectedSlate]);

  React.useEffect(() => {
    const validateCsv = async () => {
      setLineupVerified(false);
      if (((!useCustomProjections && lineupFile) || (useCustomProjections && lineupFile))) {
        const request = {
          user: auth,
          contestData: lineupFile,
          slateId: selectedSlate.slateId,
          tier: authenticated?.data?.authTier,
        };
        setParsingContest(true);
        await fetch(`${process.env.REACT_APP_API_URL}/api/contests/parsePreContestSim`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(request)
        })
          .then(res => res.json()).then(res => {
            if (res.errors) {
              setLineupFileErrors([res?.errors?.contestType, res?.errors?.contestData]);
            } else {
              setLineupVerified(true);
              setSuccessSnack(true);
              SetPoolSize(res);
            }
            setParsingContest(false)
          })
          .catch(err => {
            Sentry.captureException(err);
            console.log('err', err)
          });
      }
    };
    validateCsv().catch(err => {
      Sentry.captureException(err);
      console.log('err', err)
    });
  }, [lineupFile, useCustomProjections, selectedSite, selectedSlate])

  React.useEffect(() => {
    setIsLoading(loading);
    if (loading) {
      setTimeout(() => {
        setIsLoading(false);
      }, 360000);
    }
  }, [loading])

  React.useEffect(() => {
    if (activeStep === 1) {
      setStatusType('ContestGen')
    } else {
      setStatusType('LateSwap')
    }
    // if (activeStep === 2 && isLoading) {
    //   setTimeout(() => {
    //     setActiveStep(3)
    //   }, 15000);
    // }
    if (activeStep === 3 && !isLoading) {
      setActiveStep(4)
      setIsLoading(false);
    }
    // if (activeStep < 2) {
    //   setContestSimulating(false);
    // }
  }, [isLoading, activeStep])

  React.useEffect(() => {

    if (contestLineups.status === APIConstants.SUCCEEDED) {
      const parsedContests = contestLineups.data?.contestsInfo?.map<MenuOption<any>>(x => {
        let menuOption = {} as MenuOption<any>;

        menuOption = {
          name: x.contestName,
          value: x.contestName,
          option: {
            name: x.contestName,
            value: x.contestName,
          }
        }

        return menuOption;
      })
      const allMenuOption = {
        name: 'All',
        value: 'all',
        option: {
          name: 'All',
          value: 'all',
        }
      }

      setContests([
        allMenuOption,
        ...parsedContests
      ]);
      setSelectedContest(allMenuOption)
    }
  }, [contestLineups])

  React.useEffect(() => {
    const savelineups = async () => {
      if (contestGenLineups.data?.length > 0 && generatedSlate?.sport && generatedSlate?.type) {
        let csvData = "";

        const header = await fetch(
          `${process.env.REACT_APP_API_URL}/api/contests/csvHeaders?sport=${generatedSlate?.sport
          }&site=${generatedSlate?.type?.toUpperCase() === "SHOWDOWN"
            ? getSiteForShowdown(generatedSlate?.site)
            : generatedSlate?.site
          }`
        ).then((res) => res.json());

        csvData = header + "\n";
        contestGenLineups.data?.forEach((x) => {
          if (x.lineup?.length > 0) {
            const playerIds = x.lineup.map((player) => player.nameAndId);
            csvData = csvData.concat(playerIds.join(",") + "\n");
          }
        });
        setExportableData(csvData);
      }
    };
    savelineups();
  }, [contestGenLineups, generatedSlate]);

  React.useEffect(() => {
    setOnLoadDiversity(diversity);
    setOnLoadLineupDiversity(lineupDiversity);
  }, [])
  // Auto select first live contest
  React.useEffect(() => {
    if (liveContests && liveContests.length > 0 && !liveContestId) {
      SetLiveContestId(liveContests[0].siteContestId);
    }
    if (liveContests && liveContests.length > 0) {
      setPoolSource('LiveContest');
    }
  }, [liveContests])

  React.useEffect(() => {
    if (poolSource === 'LiveContest') {
      setContestSimulating(false);
    }
  }, [poolSize])

  // Populate playerExposureCaps from customProjections when they're set
  React.useEffect(() => {
    if (customProjections && customProjections.length > 0) {
      const exposureCaps: { [key: string]: number } = {};
      customProjections.forEach(projection => {
        if (projection.maxExposure !== undefined && projection.maxExposure !== 1) {
          exposureCaps[projection.nameAndId] = projection.maxExposure * 100;
        }
      });
      if (Object.keys(exposureCaps).length > 0) {
        setPlayerExposureCaps(exposureCaps);
      }
    }
  }, [customProjections]);

  return (
    <Stack spacing={1} width='100%' maxHeight='100%' paddingTop={1}>
      {
        lineupFileErrors && lineupFileErrors.map(e => (
          <Snackbar
            open={lineupFileErrors.length > 0}
            autoHideDuration={6500}
            onClose={() => setLineupFileErrors([])}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Alert severity='error'>
              {e}
            </Alert>
          </Snackbar>
        ))
      }
      {
        lineupVerified && (
          <Snackbar
            open={successSnack}
            onClose={() => setSuccessSnack(false)}
            autoHideDuration={5000}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            onClick={() => setSuccessSnack(false)}
          >
            <Alert severity='success'>
              File uploaded and verified successfully!
            </Alert>
          </Snackbar>
        )
      }
      {!((contestSimulating && isLoading) || activeStep === 2) && (
        <Box className={classes.heading}>
          <Stack direction='row' className={classes.generatorSettings}>
            {
              activeStep === 1 && (
                <>
                  <Stack direction="row" justifyContent="center" alignItems="start">
                    <Stack direction='column' margin='10px 10px 0px 10px' >
                      <SelectInput
                        data={poolSourceOptions(liveContestReady)}
                        label="Select Pool Source"
                        bottomLabel={toolTipText.selectPoolSource}
                        width={125}
                        value={poolSource}
                        onSelect={(selection: any) => setPoolSource(selection.value)}
                        tierLock={false}
                      />
                    </Stack>
                    <Box borderRadius="64px" padding="0px 5px" display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                      <svg className="MuiSvgIcon-root MuiStepIcon-root MuiStepIcon-active" focusable="false" viewBox="0 0 24 24" aria-hidden="true">
                        <circle cx="12" cy="12" r="12"></circle>
                        <text className="MuiStepIcon-text" x="9.5" y="16">{activeStep + 1}</text>
                      </svg>
                    </Box>
                    <Box maxWidth="200px" fontSize="14px" fontWeight="700">{getStepContent(activeStep)}</Box>
                    {poolSource === 'CustomPool' && (
                      <Box marginTop={1.5}>
                        <Upload
                          inputId="lineupsInput"
                          onUpload={(lineups: string) => setLineupFile(lineups)}
                          selectedSlate={selectedSlate}
                          label="Upload Custom Pool CSV"
                          defaultFileName={lineupFile ? 'Custom Uploaded Pool' : ''}
                          hideDropzone
                          captionUnderneath
                        />
                      </Box>
                    )}
                  </Stack>
                  <Box margin="auto 20px" height="41px">
                    <Divider orientation="vertical" style={{ backgroundColor: 'white', }} />
                  </Box>
                  <Stack margin='10px 10px 0px 10px'>
                    <LineupSettings poolSource={poolSource} />
                  </Stack>
                  <Stack alignItems="center" justifySelf="flex-end" margin="auto 0" marginLeft="auto">
                    {
                      lineupVerified ? (
                        <Button
                          onClick={() => {
                            // onSimulate();
                            // setContestSimulating(true);
                            setContestSimulating(true);
                            setIsLoading(false);
                            setActiveStep(2)
                          }}
                          className={
                            classes.goToPre +
                            (!(
                              JSON.stringify(boostedPlayers) === JSON.stringify(lastGeneratedBoosts.current) ||
                              (Object.keys(boostedPlayers).length === 0 &&
                                Object.keys(lastGeneratedBoosts.current).length === 0)
                            )
                              ? " " + classes.disabled
                              : "")
                          }
                        >
                          Review Projections
                          <ArrowForwardIcon />
                        </Button>
                      ) : (

                        <Button variant='contained' size='small' className={classes.button} onClick={() => onGenerate()} disabled={contestSimulating || parsingContest}>
                          {poolSource === 'LiveContest' ? 'View Live Pool' : 'Generate Lineups'}
                        </Button>
                      )
                    }
                  </Stack>
                </>
              )
            }
            {
              activeStep === 4 && (
                <>
                  <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                    <Box borderRadius="64px" padding="0px 5px" display="flex" flexDirection="column" justifyContent="center" alignItems="center" style={{ backgroundColor: '#0058CC' }}>
                      {activeStep + 1}
                    </Box>
                    <Box maxWidth="600px" fontSize="14px" fontWeight="700">{getStepContent(activeStep)}</Box>
                  </Stack>
                  <Box margin="auto 20px" height="41px">
                    <Divider orientation="vertical" style={{ backgroundColor: 'white', }} />
                  </Box>
                  <Stack direction='column' margin="0 10px">
                    <SelectInput
                      data={contests}
                      label="Contest"
                      bottomLabel={toolTipText.contestDropdown}
                      value={selectedContest.value}
                      onSelect={(selection: any) => setSelectedContest(selection)}
                      tierLock={false}
                    />
                  </Stack>
                  {!isLoading && activeStep === 4 && contestLineups.status === APIConstants.SUCCEEDED && (
                    <Stack spacing={1} direction='row' alignItems="center" justifySelf="flex-end" margin="auto 0" marginLeft="auto">
                      <Button className={classes.activeBoostButton} startIcon={allowDupes ? <Layers fontSize="small" /> : <LayersClear fontSize="small" />} onClick={() => setAllowDupes(!allowDupes)}>
                        <CustomToolTip title={toolTipText.duplicatesDisabled} placement='bottom'>
                          <Box>{allowDupes ? 'Duplicates Allowed' : 'Duplicates Disabled'}</Box>
                        </CustomToolTip>
                      </Button>
                      <Button className={classes.activeBoostButton} startIcon={<ExposureIcon fontSize="small" />} onClick={() => setPlayerModalOpen(true)}>
                        <CustomToolTip title="Manage player ROI boosts and exposure caps" placement='bottom'>
                          <Box>Players</Box>
                        </CustomToolTip>
                      </Button>
                      {showStackExposuresPreContest(selectedSlate) && (
                        <Button className={classes.activeBoostButton} startIcon={<ExposureIcon fontSize="small" />} onClick={() => setStackModalOpen(true)}>
                          <CustomToolTip title="Manage stack ROI boosts and exposure caps" placement='bottom'>
                            <Box>Stacks</Box>
                          </CustomToolTip>
                        </Button>
                      )}
                      <Button className={classes.activeBoostButton} startIcon={<ExposureIcon fontSize="small" />} onClick={() => setGroupsModalOpen(true)}>
                        <CustomToolTip title="Create player groups to boost lineups containing all players in the group" placement='bottom'>
                          <Box>Groups</Box>
                        </CustomToolTip>
                      </Button>
                      <QuickFavorite
                        lineups={contestLineups.data.lateSwapData}
                        players={contestLineups.data.players}
                        disabled={false}
                        contestName={selectedContest.value as string}
                        boostedPlayers={playerList}
                        boostedStacks={stackList}
                        allowDupes={allowDupes}
                        playerExposureDict={playerExposureCaps}
                        stacks={stacks()}
                        stackList={stackExposureCaps}
                      />
                    </Stack>
                  )}
                </>
              )
            }
          </Stack>
        </Box>
      )}

      {contestSimulating && (
        <>
          {isLoading && (
            <Box flex={1} minHeight={'calc(100vh - 350px)'} justifyContent='center' alignItems='center' display='flex'>
              <ProgressBar percentage={loadingPercent} contestGen />
            </Box>
          )}
          {!isLoading && activeStep === 1 && (
            <Box maxHeight='calc(100vh - 300px)' height='calc(100vh - 300px)'>
              <DataGrid
                includeStacks={false}
                lineups={contestPlayers.data}
                headers={contestGeneratorPlayerHeaderConfig(
                  handleBoost,
                  boostedPlayers,
                  selectedSlate,
                  poolSource === 'LiveContest'
                )}
                isLoading={contestPlayers.status === APIConstants.STARTED}
                selectedView="players"
                setPlayerList={() => console.log("")}
                setStackList={() => console.log("")}
                simType={"contest" + auth?.toLowerCase()}
                setStackView={(view: string) => console.log("")}
                pagination
                emptyText="Fill out settings to start generating."
                fontSize={14}
                footer={(apiRef) => footer(apiRef)}
                // compact='compact'
                backgroundColor="rgba(219, 77, 0, 0.20)"
              />
            </Box>
          )}
          {
            activeStep === 2 && (
              <EasyProjection
                selectedSlate={selectedSlate}
                onSetProjections={onSimulate}
                disabled={false}
                tableHeight="calc(100vh - 190px)"
                backgroundColor="rgba(219, 77, 0, 0.20)"
                useLiveProjections={useLiveProjections}
                setUseLiveProjections={selectedSlate.sport === 'NBA' ? setUseLiveProjections : undefined}
                simType="lateSwap"
              />
            )
          }
          {isLoading && activeStep === 4 && contestLineups.status === APIConstants.STARTED && (
            <Box width='100%' display="flex" height='100%' justifyContent='center' alignItems='center'>
              <MoonLoader color='white' />
            </Box>
          )}
          {!isLoading && activeStep === 4 && contestLineups.status === APIConstants.SUCCEEDED && (
            <Box flex={1}>
              <FinalStepTabs selectedContest={selectedContest} exportCsvHeader={exportCsvHeader} boostedPlayersList={playerList} boostedStackList={stackList} playerGroups={playerGroups} onApiRefReady={setTableApiRef} />
              <LateSwapPlayerManagementModal
                open={playerModalOpen}
                players={boostPlayers()}
                playerRoiList={playerList}
                playerExposureList={playerExposureCaps}
                setPlayerRoiList={setPlayerList}
                setPlayerExposureList={setPlayerExposureCaps}
                closeModal={() => setPlayerModalOpen(false)}
                selectedSlate={selectedSlate}
                lineups={contestLineups.data.lateSwapData}
                contestName={selectedContest.value as string}
                allowDupes={allowDupes}
                stackExposureDict={stackExposureCaps}
                customProjections={customProjections}
                apiRef={tableApiRef}
              />
              <LateSwapStackManagementModal
                open={stackModalOpen}
                stacks={stacks()}
                stackRoiList={stackList}
                stackExposureList={stackExposureCaps}
                setStackRoiList={setStackList}
                setStackExposureList={setStackExposureCaps}
                closeModal={() => setStackModalOpen(false)}
                selectedSlate={selectedSlate}
                lineups={contestLineups.data.lateSwapData}
                players={boostPlayers()}
                contestName={selectedContest.value as string}
                allowDupes={allowDupes}
                playerExposureDict={playerExposureCaps}
                playerRoiList={playerList}
                apiRef={tableApiRef}
              />
              <LateSwapPlayerGroupsModal
                open={groupsModalOpen}
                players={boostPlayers()}
                closeModal={() => setGroupsModalOpen(false)}
                selectedSlate={selectedSlate}
                lineups={contestLineups.data.lateSwapData}
                contestName={selectedContest.value as string}
                allowDupes={allowDupes}
                playerExposureDict={playerExposureCaps}
                stackExposureDict={stackExposureCaps}
                playerRoiList={playerList}
                stackRoiList={stackList}
                playerGroups={playerGroups}
                setPlayerGroups={setPlayerGroups}
              />
            </Box>
          )}
        </>
      )}
    </Stack>
  );
};
const mapStateToProps = (state: any) => {
  const {
    contestPlayers,
    contestLineups,
    contestGenLineups,
    contestStacks,
    loading,
    generatedSlate,
    boostedPlayers,
    stackConstraints,
    poolSize,
    runback,
    diversity,
    lineupDiversity,
    liveContestId,
    percentToFirst
  } = state.lateSwap;

  const {
    selectedSport,
    selectedSite,
    selectedSlate,
    authenticated
  } = state.common

  return {
    contestPlayers,
    contestLineups,
    authenticated,
    contestStacks,
    loading,
    generatedSlate,
    boostedPlayers,
    stackConstraints,
    poolSize,
    percentToFirst,
    runback,
    selectedSite,
    selectedSport,
    selectedSlate,
    contestGenLineups,
    liveContestId,
    diversity,
    lineupDiversity,
  };
};

const mapDispatchToProps = {
  SetExportableLineups: setExportableLineups,
  SetBoostedPlayer: setBoostedPlayer,
  RemoveBoostedPlayer: removeBoostedPlayer,
  TriggerContestGen: triggerContestGen,
  SetPoolSize: setPoolSize,
  SetPercentToFirst: setPercentToFirst,
  StartLoading: startLoading,
  SetLiveContestId: setLiveContestId,
  GetContestPlayers: getContestPlayers,
  GetContestLineups: getContestLineups,
};

export default connect(mapStateToProps, mapDispatchToProps)(TabsWrapper);
